import {Injectable} from '@angular/core';

import type { ZoominfoAnalytic } from '@zoominfo/zoominfo-analytic';
import {environment} from '../../../environments/environment';
import { get, isNil, omitBy } from "lodash-es";
import {LoginCookieService} from "./login-cookie.service";
import { BehaviorSubject, Observable, forkJoin, of } from 'rxjs';
import { AdaMessagingInterface } from "@ada-cx/messaging-sdk";
import { CookieCategories } from '../../utility/cookie-categories';
import { CookiePrefixes } from '../../utility/cookie-prefixes';
import { LoginConfig } from '../../login/login.config';

export enum LoginPageType {
  MAIN = 'Main',
  MAIN_REACHOUT = 'Main Reachout',
  SFNA = 'SFNA',
  MDNA = 'MDNA',
  ENT_MOBILE = 'Enterprise Mobile',
  GTMC = 'GTMC',  // ZI Chat
  DESKTOP_CWS = 'Desktop CWS',  // GTM Workspace desktop
}

export enum LoginType {
  CREDENTIALS = 'Credentials',
  GOOGLE_SOCIAL_AUTH = 'Google Social Auth',
  MS_SOCIAL_AUTH = 'Microsoft Social Auth',
  SSO = 'SSO',
}

export declare var uxtrack;

const DATADOG_CLIENT_TOKEN = 'pub141b561e8db9405ecbedfb77d0869f3d';
const DATADOG_SITE = 'datadoghq.com';
const DATADOG_SERVICE = 'login-ui';  // matches the login-ui Service Catalog entry

/**
 * Analytics Service
 */
@Injectable({
  providedIn: 'root'
})
export class AnalyticsService {
  // use separate variables for page load and sign in so there is no conflict with the timers
  private pageLoadStartTime: number;
  private pageLoadEndTime: number;
  private loginFormLoadEndTime: number;
  private oktaWidgetLoadEndTime: number;
  // separate from pageLoadStartTime/pageLoadEndTime above: the SFNA (Salesforce plugin) flow measures
  // load time from its own "Continue" click rather than app navigation start, and shares this service's
  // singleton instance with the regular login flow, so it must not overwrite the regular flow's timers.
  private sfnaPageLoadStartTime: number;
  private sfnaPageLoadEndTime: number;
  private signInStartTime: number | undefined;  // also persisted to localStorage since login redirects (e.g. SSO) reload the page and reset instance state
  private signInEndTime: number;  // time when Okta login finishes (ms)
  private sessionVerifyEndTime: number;  // time when session was verified after Okta login redirection (ms)
  private used2FA = false;  // set to true if 2fa was used
  private zoominfoAnalytic: ZoominfoAnalytic;
  private amplitudeProxyInstance: any;  // send event to the proxy instance
  private datadogLogs: any;
  private datadogRum: any;
  private _amplitudeLoadedSource = new BehaviorSubject<boolean>(false);
  private _zoominfoAnalyticLoadedSource = new BehaviorSubject<boolean>(false);
  private _datadogLogsLoadedSource = new BehaviorSubject<boolean>(false);
  private _adaEmbedLoadedSource = new BehaviorSubject<boolean>(false);
  private _allCookiesAllowed = true;  // enable/disable using non-essential cookies
  private amplitudeLoaded$ = this._amplitudeLoadedSource.asObservable();
  private zoominfoAnalyticLoaded$ = this._zoominfoAnalyticLoadedSource.asObservable();
  private datadogLoggerLoaded$ = this._datadogLogsLoadedSource.asObservable();
  private adaEmbedLoaded$ = this._adaEmbedLoadedSource.asObservable();
  private _logPageLoadTime = true;  // enable sending page load time log
  private pageLoadTimeSent = false;  // prevent another log from being sent
  private adaEmbed: AdaMessagingInterface;

  private readonly PROXY_INSTANCE_NAME = 'proxy_instance';
  private readonly AMPLITUDE_EVENT_TIMEOUT = 1500;  // 1.5 seconds
  readonly PAGE_LOAD_TIME_EVENT_NAME = 'LoginPageLoadTime';
  readonly PAGE_LOAD_THRESHOLD = 60000;  // 1 min
  readonly LOGIN_TIME_THRESHOLD = 5 * 60 * 1000;  // 5 min - login shouldn't reasonably take longer than this

  constructor(private loginCookieService: LoginCookieService) {
    if (window.performance) {
      // window.performance.timing is deprecated, use window.performance.timeOrigin instead
      this.pageLoadStartTime = window.performance.timeOrigin;  // close to window.performance.timing.navigationStart
    }
    const env: string = environment.name;
    import ('@amplitude/analytics-browser').then((Amplitude) => {
      this.amplitudeProxyInstance = Amplitude.createInstance();
      this.amplitudeProxyInstance?.init(environment.amplitude_api_key, {
        instanceName: this.PROXY_INSTANCE_NAME,
        serverUrl: environment.amplitude_proxy_url,
        defaultTracking: false,
        identityStorage: this._allCookiesAllowed ? 'cookies' : 'localStorage'
      });

      console.log('Resetting Amplitude user...');
      this.amplitudeProxyInstance?.reset();  // anonymize user
      this._amplitudeLoadedSource.next(true);
      this._amplitudeLoadedSource.complete();
    });
    import('@zoominfo/zoominfo-analytic').then((ZoominfoAnalytic) => {
      this.zoominfoAnalytic = ZoominfoAnalytic;
      this._zoominfoAnalyticLoadedSource.next(true);
      this._zoominfoAnalyticLoadedSource.complete();
    });
    import('@datadog/browser-logs').then((datadogLogs) => {
      this.datadogLogs = datadogLogs?.datadogLogs;
      this.datadogLogs?.init({
        clientToken: DATADOG_CLIENT_TOKEN,
        site: DATADOG_SITE,
        service: DATADOG_SERVICE,
        env: env,
        forwardErrorsToLogs: false,
        sessionSampleRate: this.getSampleRate(env),
        trackingConsent: this._allCookiesAllowed ? 'granted' : 'not-granted'
      });
      this._datadogLogsLoadedSource.next(true);
      this._datadogLogsLoadedSource.complete();
    });
    import('@datadog/browser-rum').then((datadogRum) => this.initDatadogRum(datadogRum?.datadogRum));
    this.initAda(null);

    this.listenForConsentChange();
  }

  getSampleRate(env: string): number {
    return env === 'production' ? 1 : 100;
  }

  /** Initialize Datadog RUM. It stays off until the Browser RUM application id is configured for this environment. */
  initDatadogRum(datadogRum: any): void {
    if (!environment.datadog_rum_application_id) {
      return;
    }
    this.datadogRum = datadogRum;
    this.datadogRum?.init({
      applicationId: environment.datadog_rum_application_id,
      clientToken: DATADOG_CLIENT_TOKEN,
      site: DATADOG_SITE,
      service: DATADOG_SERVICE,
      env: environment.name,
      sessionSampleRate: this.getSampleRate(environment.name),
      sessionReplaySampleRate: 0,
      trackUserInteractions: true,
      trackResources: true,
      trackLongTasks: true,
      enablePrivacyForActionName: true,  // action names come from element text, which can include user input
      allowedTracingUrls: this.getTracingUrls(),
      beforeSend: (event) => this.sanitizeRumEvent(event),
      defaultPrivacyLevel: 'mask-user-input',
      trackingConsent: this._allCookiesAllowed ? 'granted' : 'not-granted'
    });
  }

  /** Strip query strings and fragments from RUM urls: they can carry auth codes and usernames. Never drops an event. */
  sanitizeRumEvent(event: any): boolean {
    const strip = (url?: string) => url?.split(/[?#]/)[0];
    if (event?.view) {
      event.view.url = strip(event.view.url);
      if (event.view.referrer) {
        event.view.referrer = strip(event.view.referrer);
      }
    }
    if (event?.resource?.url) {
      event.resource.url = strip(event.resource.url);
    }
    return true;
  }

  /**
   * Same-origin backend urls that receive trace headers so RUM sessions link to backend APM traces.
   * The cross-origin gw-app urls are left out until the gateway CORS allows the x-datadog-* and traceparent headers.
   */
  getTracingUrls(): string[] {
    return [
      environment.login_backend_domain,
      environment.new_login_backend_path,
      environment.sm_ping_url,
      environment.sm_refresh_access_token_url,
      environment.sm_refresh_storage_url,
      environment.sm_uuid_username_url,
      environment.ums_password_reset_url
    ].filter((url) => !!url && url.startsWith(environment.app_domain));
  }

  set logPageLoadTime(enable: boolean) {
    this._logPageLoadTime = enable;
  }

  /**
   * Fires an analytics event to the analytics services.
   * @param eventName {String} The name of the event to fire
   * @param eventProperties {Object} A set of properties that are related to  to this event.
   * @example
   * sendAmplitudeEvent('OpenedTool', {
   *      source: 'Login',
   *      tool: 'TargetAccounts'
   *    });
   */
  public sendAmplitudeEvent(eventName: string, eventProperties: object, redirect?: boolean): Promise<[object, object]> {
    const timeout = redirect ? this.AMPLITUDE_EVENT_TIMEOUT : 3000;  // use shorter timeout if redirecting to app
    const awaitTimeout = (delay, reason) =>
      new Promise<void>((resolve, reject) =>
        setTimeout(
          () => (reason === undefined ? resolve() : reject(reason)),
          delay
        )
      );
    // reject the promise on timeout
    const wrapPromise = (promise, delay, reason) =>
      Promise.race([promise, awaitTimeout(delay, reason)]);

    return new Promise((resolve, reject) => {
      this.amplitudeReadyCheck().subscribe({
        next: ([amplitudeLoaded, zoominfoAnalyticLoaded]) => {
          if (amplitudeLoaded && zoominfoAnalyticLoaded) {
            try {
              const event = this.zoominfoAnalytic?.events?.make(eventName, eventProperties);
              console.log('Sending Analytics event:', eventName, eventProperties);

              // wait for response from proxy until timeout
              resolve(wrapPromise(this.amplitudeProxyInstance?.track(event.name, event.properties).promise, timeout, { reason: 'timeout' }));
            } catch (err) {
              reject(err);
            }
          }
        },
        error: err => reject(err)
      });
    })
  }

  /**
   * This method is responsible to send GA events
   * **/
  public sendGAEvent(event) {
    if (this._allCookiesAllowed) {
      window['dataLayer'] = window['dataLayer'] || [];
      window['dataLayer'].push(event);
    }
  }

  /**
   * This method is responsible to set the user's zoom ID on the analytics providers
   * **/
  public setUserId({name = '', id = '', email = ''}) {
    console.log('Setting Amplitude user id...');
    this.amplitudeProxyInstance?.setUserId(id);
  }

  /**
   * This method sends a custom event to DD
   * **/
  public sendDataDogEvent(eventName, eventProperties) {
    this.datadogRum?.addAction(eventName, eventProperties);
  }

  // sends log to DD
  public logDataDogEvent(eventName, eventProperties) {
    this.datadogLogs?.logger.info(eventName, eventProperties);
  }

  initAda(userDetails): void {
    const email = get(userDetails, 'email', null) || this.loginCookieService.getCookie('userEmail') || null;
    const metaFields = omitBy({
      username: email,
      email,
    }, isNil);

    import('@ada-cx/messaging-sdk').then(({ loadAdaMessaging }) => {
      return loadAdaMessaging({
        handle: "gen-zoominfo",
        metaFields,
        eventCallbacks: {
          // This should match the name of the Fire Event block in Ada
          login_troubleshooting: (event) => {
            console.log('login_troubleshooting was triggered', event);
            this.adaEmbed?.triggerAnswer(LoginConfig.ADA_SUPPORT_ANSWER_ID);
          }
        }
      });
    }).then((ada) => {
      this.adaEmbed = ada;
      this._adaEmbedLoadedSource.next(true);
      this._adaEmbedLoadedSource.complete();
    }).catch((err) => {
      console.warn('Failed to initialize Ada', err);
      this._adaEmbedLoadedSource.next(false);
      this._adaEmbedLoadedSource.complete();
    });
  }

  setAdaUserDetails(userDetails): void {
    const email = get(userDetails, 'email', null) || this.loginCookieService.getCookie('userEmail') || null;
    const metaFields = omitBy({
      username: email,
      phone_number: get(userDetails, 'phone', null),
      email,
      name: get(userDetails, 'name', null)
    }, isNil);
    this.adaEmbed?.setMetaFields(metaFields);
  }

  /** Open/close the Ada chat window. */
  toggleAdaChatBot(): void {
    this.adaEmbed?.toggle();
  }

  resetAdaChatBot(): void {
    this.adaEmbed?.reset().then(() => {
      console.log('Ada chat was reset');
    });
  }

  sendErrorToAdaSupport(error: string): void {
    this.adaEmbed?.triggerProactive({ messageKey: 'login_support' });
    // Set metadata (e.g., login error message to pass into Ada)
    this.adaEmbed?.setMetaFields({ login_error: error, source: this.getLoginPageType() });
  }

  /** Delay is in milliseconds. */
  sendErrorToAdaSupporAfterDelay(error: string, delay: number): void {
    setTimeout(() => {
      this.sendErrorToAdaSupport(error);
    }, delay);
  }

  endPageLoadTimer() {
    this.pageLoadEndTime = new Date().getTime();
  }

  /** Call this when the SFNA "Continue" button is clicked, to start measuring load time for that flow. */
  startSfnaPageLoadTimer() {
    this.sfnaPageLoadStartTime = new Date().getTime();
  }

  endSfnaPageLoadTimer() {
    this.sfnaPageLoadEndTime = new Date().getTime();
  }

  /** Call this LoginFormComponent finishes loading. */
  endLoginFormLoadTimer() {
    this.loginFormLoadEndTime = new Date().getTime();
  }

  /** Call this when Okta Widget finishes loading. */
  endOktaWidgetLoadTimer() {
    this.oktaWidgetLoadEndTime = new Date().getTime();
  }

  getPageLoadStartTime(): number {
    return this.pageLoadStartTime;
  }

  getPageLoadEndTime(): number {
    return this.pageLoadEndTime;
  }

  getPageLoadTime(): number {
    return this.pageLoadEndTime - this.pageLoadStartTime;
  }

  /** Get the load time of the SFNA flow, measured from its own "Continue" click rather than pageLoadStartTime. */
  getSfnaPageLoadTime(): number {
    return this.sfnaPageLoadEndTime - this.sfnaPageLoadStartTime;
  }

  getOktaWidgetLoadTime(): number {
    return this.oktaWidgetLoadEndTime - this.pageLoadStartTime;
  }

  /** Get the load time of LoginFormComponent. */
  getLoginFormLoadTime(): number {
    return this.loginFormLoadEndTime - this.pageLoadStartTime;
  }

  // single source of truth for mapping login context flags to a LoginPageType.
  // used both for the LoginPageLoadTime log below and for tagging LoginTime as soon as tracking starts.
  resolveLoginPageType(isReachout: boolean, isEnterpriseMobileLogin: boolean, isGtmcLogin: boolean, isDesktopLogin: boolean, isMdna: boolean = false, isSfna: boolean = false): LoginPageType {
    if (isSfna) {
      return LoginPageType.SFNA;
    } else if (isReachout) {
      return LoginPageType.MAIN_REACHOUT;
    } else if (isMdna) {
      return LoginPageType.MDNA;
    } else if (isEnterpriseMobileLogin) {
      return LoginPageType.ENT_MOBILE;
    } else if (isGtmcLogin) {
      return LoginPageType.GTMC;
    } else if (isDesktopLogin) {
      return LoginPageType.DESKTOP_CWS;
    } else {
      return LoginPageType.MAIN;
    }
  }

  /** Send LoginPageLoadTime DD log.
   *  TTI: time to interactive (time it takes for the login form to be interactive)
   *  pageLoadTime: time it takes for the page to load (excluding Okta Widget and ADA chat bubble)
   *  oktaWidgetLoadTime: time it takes for the Okta Widget to load
   */
  sendLoginPageLoadTimeLog(isReachout: boolean, isEnterpriseMobileLogin: boolean, isGtmcLogin: boolean, isDesktopLogin: boolean, isMdna: boolean = false) {
    // Prevent additional logs from being sent to handle inaccurate time when navigating to different paths without page reload.
    if (this.pageLoadTimeSent) {
      return;
    }

    const loginFormComponentLoadTime = this.getLoginFormLoadTime();
    const oktaWidgetLoadTime: number = this.getOktaWidgetLoadTime();
    const pageLoadTime = this.getPageLoadTime();
    const type: LoginPageType = this.resolveLoginPageType(isReachout, isEnterpriseMobileLogin, isGtmcLogin, isDesktopLogin, isMdna);
    const eventProperties = { type, tti: loginFormComponentLoadTime, pageLoadTime, oktaWidgetLoadTime };

    this.printPageLoadTime(type, loginFormComponentLoadTime, pageLoadTime, oktaWidgetLoadTime);
    this.setLoginPageType(type);
    // send logs to DD if page load time is valid
    if (this.validPageLoadTime(type, loginFormComponentLoadTime, pageLoadTime, oktaWidgetLoadTime) && this._logPageLoadTime) {
      this.logDataDogEvent(this.PAGE_LOAD_TIME_EVENT_NAME, eventProperties);
      this.pageLoadTimeSent = true;
    }
  }

  /*
  ** Load times should be greater than 0 ms and page type should exist. tti shouldn't be higher than pageLoadTime.
  ** Not a valid time if loading is unrealistically long, this metric should not be affected by outside factors
  ** (e.g. internet connection issue, browser frozen for a time, etc.)
  ** Slow internet: valid, but 1 minute is still unrealistic
  ** Browser frozen: invalid, it could freeze for a long time
  */
  validPageLoadTime(type: LoginPageType, tti: number, pageLoadTime: number, oktaWidgetLoadTime: number) {
    return type && tti > 0 && tti < this.PAGE_LOAD_THRESHOLD && tti <= pageLoadTime &&
      oktaWidgetLoadTime > 0 && oktaWidgetLoadTime < this.PAGE_LOAD_THRESHOLD && pageLoadTime <= oktaWidgetLoadTime &&
      pageLoadTime > 0 && pageLoadTime < this.PAGE_LOAD_THRESHOLD;
  }

  private printPageLoadTime(type: LoginPageType, tti: number, pageLoadTime: number, oktaWidgetLoadTime: number) {
    console.log(`login page type: ${type}`);
    console.log(`TTI (time to interactive): ${tti} ms`);
    console.log(`pageLoadTime (excluding Okta Widget and ADA chat bubble): ${pageLoadTime} ms`);
    console.log(`oktaWidgetLoadTime: ${oktaWidgetLoadTime} ms`);
  }

  startSignInTimer() {
    // Persist to localStorage too since some login flows (e.g. SSO) redirect through
    // Okta and reload the page, which resets this instance and its in-memory start time.
    this.signInStartTime = new Date().getTime();
    localStorage.setItem('signInStartTime', String(this.signInStartTime));
  }

  getStartSignInTime(): number {
    // Prefer the in-memory start time if this instance already has one (e.g. localStorage got
    // cleared elsewhere mid-login) and only fall back to localStorage for the redirect case above.
    if (this.signInStartTime !== undefined) {
      return this.signInStartTime;
    }
    const storedStartTime = localStorage.getItem('signInStartTime');
    // explicitly handle the missing case instead of relying on Number(null) === 0
    return storedStartTime !== null ? Number(storedStartTime) : 0;
  }

  endSignInTimer() {
    this.signInEndTime = new Date().getTime();
  }

  endSessionVerifyTimer() {
    this.sessionVerifyEndTime = new Date().getTime();
  }

  // get login time in milliseconds
  getSignInTime(): number {
    return this.signInEndTime - this.getStartSignInTime();
  }

  // get the time it took to verify session including login time in milliseconds
  getSessionVerifyTime(): number {
    return this.sessionVerifyEndTime - this.getStartSignInTime();
  }

  setLoginType(type: LoginType) {
    localStorage.setItem('loginType', type);
  }

  getLoginType(): LoginType {
    return this.getLoginTypeFromStr(localStorage.getItem('loginType'));
  }

  // convert login type name to LoginType enum
  getLoginTypeFromStr(typeStr: string): LoginType {
    // default login type: credentials
    return Object.values(LoginType).some((val: string) => val === typeStr) ? typeStr as LoginType : LoginType.CREDENTIALS;
  }

  // convert login type name to LoginType enum
  getLoginPageTypeFromStr(typeStr: string): LoginPageType {
    // default page: main
    return Object.values(LoginPageType).some((val: string) => val === typeStr) ? typeStr as LoginPageType : LoginPageType.MAIN;
  }

  setLoginPageType(loginPageType: LoginPageType) {
    localStorage.setItem('loginPageType', loginPageType);
  }

  getLoginPageType(): LoginPageType {
    return this.getLoginPageTypeFromStr(localStorage.getItem('loginPageType'));
  }

  // determine if 2fa was used
  setUsed2FA(oktaTokenStorage) {
    this.used2FA = oktaTokenStorage ? oktaTokenStorage.idToken.claims.used2FA : false;
  }

  getUsed2FA(): boolean {
    return this.used2FA;
  }

  handleAppLauncherLoaded() {
    try {
      uxtrack.end({
        action : 'SignInToAppLauncher'
      });
    } catch (err) {
      console.log(err);
    }
  }

  sendLoginTimeToDD() {
    let loginType: LoginType = this.getLoginType();
    let loginPageType: LoginPageType = this.getLoginPageType();
    const used2FA: boolean = this.getUsed2FA();
    const loginTime: number = this.getSignInTime();
    const sessionVerifyTime = this.getSessionVerifyTime();
    console.log(`loginTime: ${loginTime} ms`);
    try {
      uxtrack.end({
        action : 'LoginTime',
        loginPageType,
        loginType
      });
    } catch (err) {
      console.log(err);
    }

    // only send logs if we got a valid time
    if (this.validLoginTime(loginTime)) {
      if (loginType === LoginType.CREDENTIALS) {
        this.logDataDogEvent('LoginTime', { loginPageType, loginType, loginTime, sessionVerifyTime, used2FA });
      } else {
        this.logDataDogEvent('LoginTime', { loginPageType, loginType, loginTime, sessionVerifyTime });
      }
    }

    this.signInStartTime = undefined;
    localStorage.removeItem('loginType');
    localStorage.removeItem('loginPageType');
    localStorage.removeItem('signInStartTime');
  }

  /*
  ** Login time should be greater than 0 ms and less than the threshold.
  ** Not a valid time if it's unrealistically long, e.g. the start time wasn't captured
  ** (missing/cleared localStorage) and the end time got used as the elapsed time instead.
  */
  validLoginTime(loginTime: number): boolean {
    return loginTime > 0 && loginTime < this.LOGIN_TIME_THRESHOLD;
  }

  startTrackingLoginTime() {
    const loginPageType: LoginPageType = this.getLoginPageType();
    const loginType: LoginType = this.getLoginType();

    try {
      uxtrack.start({
        action : 'LoginTime',
        loginPageType,
        loginType,
        used2FA : false
      });
      uxtrack.start({
        action : 'SignInToAppLauncher',
        loginPageType,
        loginType,
        used2FA : false
      });
    } catch (err) {
      console.log(err);
    }
  }

  trackAppLoadStartTime() {
    try {
      uxtrack.start({
        action : 'AppLoadTime'
      });
    } catch (err) {
      console.log(err);
    }
  }

  // loginPageType is set here (synchronously, at login-attempt time) rather than relying solely on
  // sendLoginPageLoadTimeLog, which only runs once Datadog has finished loading. If that load is slow
  // (or never resolves), startTrackingLoginTime()/sendLoginTimeToDD() would read a stale/missing
  // loginPageType from localStorage and silently fall back to LoginPageType.MAIN.
  startLoginTracking(loginType: LoginType, loginPageType?: LoginPageType) {
    this.startSignInTimer();
    this.setLoginType(loginType);
    if (loginPageType) {
      this.setLoginPageType(loginPageType);
    }
    this.startTrackingLoginTime();
    this.trackAppLoadStartTime();
  }

  updateLogger2faFlag(used2FA: boolean) {
    try {
      uxtrack.update({
        action : 'LoginTime',
        used2FA : used2FA
      });
      uxtrack.update({
        action : 'SignInToAppLauncher',
        used2FA : used2FA
      });
      uxtrack.update({
        action : 'AppLoadTime',
        used2FA : used2FA
      });
    } catch (err) {
      console.log(err);
    }
  }

  updateLogPlatformInfo(isAppLauncher: boolean, platform?: string) {
    try {
      uxtrack.update({
        action : 'AppLoadTime',
        app : platform,
        isAppLauncher
      });
    } catch (err) {
      console.log(err);
    }
  }

  // return the source or origin value of login page to send to amplitude
  getLoginSource(isReachout: boolean): string {
    return isReachout ?'ChromeExtensionLogin' : 'WebLogin';
  }

  /* istanbul ignore next */
  /** Listen for OneTrust consent change when ready. */
  listenForConsentChange(): void {
    // do not reload for initial check since it happens automatically
    this.handleConsentConfirmation();  // check current consent
    const id = window.setInterval(() => {
      if (!!window['OneTrust'] && !!window['OneTrust'].OnConsentChanged) {
        const allowAllBtn = document.getElementById('onetrust-accept-btn-handler') as HTMLButtonElement;
        const acceptRecommendedBtn = document.getElementById('accept-recommended-btn-handler') as HTMLButtonElement;
        const rejectBtn = document.querySelector('.ot-pc-refuse-all-handler') as HTMLButtonElement;
        const confirmBtn = document.querySelector('.save-preference-btn-handler.onetrust-close-btn-handler') as HTMLButtonElement;

        // allowAllBtn only loads for the initial consent. acceptRecommendedBtn, rejectBtn, and confirmBtn will always load.
        if (rejectBtn && confirmBtn && acceptRecommendedBtn) {
          window.clearInterval(id);
        }
        this.oneTrustBtnClickListener(allowAllBtn);
        this.oneTrustBtnClickListener(acceptRecommendedBtn);
        this.oneTrustBtnClickListener(rejectBtn);
        this.oneTrustBtnClickListener(confirmBtn);
      }
    }, 100);
  }

  /** Handle OneTrust consent confirmation. This is invoked even if there was no change. */
  handleConsentConfirmation(): void {
    const prevAllCookiesAllowed = localStorage.getItem(LoginConfig.ONETRUST_ALLOW_ALL_LOCAL_STORAGE) === 'true';
    this._allCookiesAllowed = this.isAllowAllCookies();
    localStorage.setItem(LoginConfig.ONETRUST_ALLOW_ALL_LOCAL_STORAGE, String(this._allCookiesAllowed));  // store consent to handle page reloads
    setTimeout(() => {
      if (!this._allCookiesAllowed) {
        this.loginCookieService.deleteCookiesByPrefix(CookiePrefixes.AMPLITUDE);  // amplitude with hostname as domain
        this.loginCookieService.deleteCookiesByPrefix(CookiePrefixes.AMPLITUDE, '/', environment.cookies_domain);  // amplitude
        this.loginCookieService.deleteCookiesByPrefix(CookiePrefixes.GOOGLE_ANALYTICS, '/', environment.cookies_domain);  // google analytics
        // _dd_s cookie cannot be deleted until reloading the page. Disable tracking until the next page visit.
        if (prevAllCookiesAllowed && (this.datadogLogs || this.datadogRum)) {
          this.datadogLogs?.setTrackingConsent('not-granted');
          this.datadogRum?.setTrackingConsent('not-granted');
        } else {
          this.loginCookieService.deleteCookiesByPrefix(CookiePrefixes.DATADOG);  // datadog with hostname as domain
        }
      }
    }, 500);  // delete cookies after delay to catch cookies that are set after the consent change
  }

  /** Listen for the click event if the button exists. */
  oneTrustBtnClickListener(btn: HTMLButtonElement) {
    if (btn) {
      btn.addEventListener('click', () => {
        this.handleConsentConfirmation();
        window.location.reload();  // reload page after consent confirmation
      });
    }
  }

  /** Check if the user allowed all cookies in the OneTrust banner. */
  isAllowAllCookies(): boolean {
    const optanonConsent = this.loginCookieService.getCookie(LoginConfig.ONETRUST_CONSENT_COOKIE);
    return optanonConsent && optanonConsent.includes(`${CookieCategories.PERFORMANCE}:1`) && optanonConsent.includes(`${CookieCategories.TARGETING}:1`);
  }

  /** The observable will push [true, true] to the observer when the service is ready to send Amplitude events. */
  amplitudeReadyCheck(): Observable<[boolean, boolean]> {
    if (this._amplitudeLoadedSource.getValue() && this._zoominfoAnalyticLoadedSource.getValue()) {
      return of([true, true]);
    }

    return forkJoin([
      this.amplitudeLoaded$,
      this.zoominfoAnalyticLoaded$,
    ]);
  }

  /** Wait for Datadog logger to be ready. */
  datadogLogsReadyCheck(): Observable<boolean> {
    if (this._datadogLogsLoadedSource.getValue()) {
      return of(true);
    }
    return this.datadogLoggerLoaded$;
  }

  /** Wait for Ada embed to be ready. */
  adaEmbedReadyCheck(): Observable<boolean> {
    if (this._adaEmbedLoadedSource.getValue()) {
      return of(true);
    }
    return this.adaEmbedLoaded$;
  }
}
